/**
 * `ui/Avatar.tsx` — the shared circular person disc (#1713).
 *
 * Before this existed the SPA carried three ad-hoc initials renderers
 * (`shell/UserPill`, `routes/team/MembersTableRow.MemberAvatar`, and the
 * theme-coupled `taskmap/components/Avatar`). This is the product-side
 * primitive: one disc, three sizes, an optional photo with an **initials
 * fallback**, and a deterministic per-person tone so the same face reads
 * the same everywhere it appears.
 *
 * DESIGN REGISTER (the locked editorial / Field Desk frame): a hairline
 * ring in `--color-border` and a flat fill — **no drop shadow**, no
 * gradient, one radius (a circle). The identity tone comes from the same
 * accessible, theme-independent palette the roster already uses for
 * people (`lib/personColors.ts` → `lib/stationColors.ts` specs), so a
 * member's disc, their station-cell band outline, and their timeline
 * segment all sit in one hue family.
 *
 * INITIALS follow the roster's abbreviation convention
 * (`lib/abbreviations.ts::deriveAbbreviation` — initials of up to three
 * words, single-word names take leading letters), capped at two glyphs so
 * a 20px disc stays legible.
 *
 * ACCESSIBILITY: by default the disc is an image with the person's name
 * as its accessible name (`role="img"` + `aria-label`). Pass `decorative`
 * where the name is already rendered immediately beside the disc (the
 * roster member header, the station-cell name chips) so a screen reader
 * announces the person once, not twice.
 *
 * NOT a photo-upload surface: no member photo field exists end-to-end yet
 * (no object storage in the stack), so `imageUrl` is here for the day it
 * does — every current call site falls through to initials.
 */

import { useState } from "react";

import { deriveAbbreviation } from "../../lib/abbreviations";
import { cn } from "../../lib/cn";
import { personColor } from "../../lib/personColors";

export type AvatarSize = "sm" | "md" | "lg";

/**
 * The disc's fill treatment.
 *
 * - `identity` (default) — the person's deterministic palette pastel with
 *   its matching dark ink. Use wherever the disc stands for a specific
 *   human in a list of humans.
 * - `placeholder` — a calm dashed ring on a subtle surface: "this row is
 *   a manual entry, nobody has claimed it yet".
 * - `accent` — the theme accent (the team lead).
 * - `account` — the fixed indigo that marks a linked member account.
 */
export type AvatarTone = "identity" | "placeholder" | "accent" | "account";

/** Disc geometry per size. sm ≈ 20px, md ≈ 28px, lg ≈ 36px. */
const SIZE_CLASS: Record<AvatarSize, string> = {
  sm: "h-5 w-5 text-[8px]",
  md: "h-7 w-7 text-[10px]",
  lg: "h-9 w-9 text-xs",
};

const TONE_CLASS: Record<Exclude<AvatarTone, "identity">, string> = {
  placeholder: "border-dashed bg-bg-subtle text-fg-subtle",
  accent: "bg-accent text-fg-on-accent",
  account: "bg-[#4F46E5] text-white",
};

export interface AvatarProps {
  /**
   * The person's FULL display name. Drives both the initials and the
   * deterministic identity tone, so pass the full name even when the
   * surface renders an abbreviation — the disc then stays stable when
   * the roster's abbreviation toggle flips.
   */
  name: string;
  /** Optional photo. Falls back to initials when absent or on load error.
   *  `undefined` is accepted explicitly (exactOptionalPropertyTypes) so
   *  call sites can pass `row?.avatar_url` straight through (#1735). */
  imageUrl?: string | null | undefined;
  /** Disc size. Defaults to `md`. */
  size?: AvatarSize;
  /** Fill treatment. Defaults to `identity`. */
  tone?: AvatarTone;
  /**
   * Hide the disc from assistive tech. Set this when the person's name is
   * already rendered beside it.
   */
  decorative?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** Up to two glyphs, following the roster abbreviation convention. */
export function avatarInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === "") return "?";
  return deriveAbbreviation(trimmed).slice(0, 2);
}

export function Avatar({
  name,
  imageUrl,
  size = "md",
  tone = "identity",
  decorative = false,
  className,
  "data-testid": testId,
}: AvatarProps) {
  // A broken photo URL must never leave a blank disc — fall back to the
  // initials the moment the image errors.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!imageUrl && !imageFailed;
  const identity = tone === "identity" ? personColor(name) : null;

  const a11y = decorative
    ? ({ "aria-hidden": true } as const)
    : ({ role: "img", "aria-label": name } as const);

  return (
    <span
      {...a11y}
      data-testid={testId}
      className={cn(
        // Hairline ring + flat fill; circular is the one radius here.
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border font-semibold leading-none",
        SIZE_CLASS[size],
        tone === "identity" ? undefined : TONE_CLASS[tone],
        className,
      )}
      style={identity ? { background: identity.fill, color: identity.text } : undefined}
    >
      {showImage ? (
        <img
          src={imageUrl as string}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        avatarInitials(name)
      )}
    </span>
  );
}
